import { Separator } from '@/components/plate-ui/separator';
import { buttonVariants } from '@/components/ui/button';
import { type UseVirtualFloatingOptions, flip, offset } from '@platejs/floating';
import { getLinkAttributes } from '@platejs/link';
import {
  type LinkFloatingToolbarState,
  FloatingLinkUrlInput,
  useFloatingLinkEdit,
  useFloatingLinkEditState,
  useFloatingLinkInsert,
  useFloatingLinkInsertState,
} from '@platejs/link/react';
import { cva } from 'class-variance-authority';
import HubertLabel from 'components/HubertUI/HubertLabel';
import HubertSwitch from 'components/HubertUI/HubertSwitch';
import { ExternalLink, Link, Text, Unlink } from 'lucide-react';
import type { TLinkElement } from 'platejs';
import { KEYS } from 'platejs';
import { useEditorRef, useEditorSelection, useFormInputProps } from 'platejs/react';
import { useEffect, useMemo, useState } from 'react';

const popoverVariants = cva(
  'z-50 w-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md outline-hidden',
);

const inputVariants = cva(
  'flex h-[28px] w-full rounded-md border-none bg-transparent px-1.5 py-1 text-base placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-transparent md:text-sm',
);

export const LinkFloatingToolbar = ({ state }: { state?: LinkFloatingToolbarState }) => {
  const floatingOptions: UseVirtualFloatingOptions = {
    middleware: [
      offset(8),
      flip({
        fallbackStrategy: 'initialPlacement',
      }),
    ],
    placement: 'bottom-start',
  };

  const insertState = useFloatingLinkInsertState({
    ...state,
    floatingOptions: {
      ...floatingOptions,
      ...state?.floatingOptions,
    },
  });
  const { hidden, props: insertProps, ref: insertRef, textInputProps } = useFloatingLinkInsert(insertState);

  const editState = useFloatingLinkEditState({
    ...state,
    floatingOptions: {
      ...floatingOptions,
      ...state?.floatingOptions,
    },
  });
  const { editButtonProps, props: editProps, ref: editRef, unlinkButtonProps } = useFloatingLinkEdit(editState);
  const inputProps = useFormInputProps({
    preventDefaultOnEnterKeydown: true,
  });

  const editor = useEditorRef();

  const linkEntry = editor?.api.above({ match: { type: KEYS.link } }) as any;
  const currentIsButton = !!(linkEntry && (linkEntry[0] as any).renderType === 'button');

  const [isButtonChecked, setIsButtonChecked] = useState(currentIsButton);

  useEffect(() => {
    setIsButtonChecked(currentIsButton);
  }, [currentIsButton]);

  const handleToggleButton = (checked: boolean) => {
    console.log('handleToggleButton', checked);
    setIsButtonChecked(checked);

    if (linkEntry) {
      const [, path] = linkEntry;
      editor.tf.setNodes({ renderType: checked ? 'button' : undefined }, { at: path });
    }
  };

  if (hidden) return null;

  const asButton = (
    <div className="flex flex-row items-center justify-start gap-2 p-1">
      <HubertSwitch id="as-button-switch" checked={isButtonChecked} onCheckedChange={handleToggleButton} />
      <HubertLabel htmlFor="as-button-switch" className="cursor-pointer text-sm">
        As button
      </HubertLabel>
    </div>
  );

  const input = (
    <div className="flex w-[330px] flex-col" {...inputProps}>
      <div className="flex items-center">
        <div className="text-muted-foreground flex items-center pr-1 pl-2">
          <Link className="size-4" />
        </div>

        <FloatingLinkUrlInput className={inputVariants()} placeholder="Paste link" data-plate-focus />
      </div>
      <Separator className="my-1" />
      <div className="flex items-center">
        <div className="text-muted-foreground flex items-center pr-1 pl-2">
          <Text className="size-4" />
        </div>
        <input className={inputVariants()} placeholder="Text to display" data-plate-focus {...textInputProps} />
      </div>
    </div>
  );

  const editContent = editState.isEditing ? (
    input
  ) : (
    <div className="box-content flex items-center">
      <button className={buttonVariants({ size: 'sm', variant: 'ghost' })} type="button" {...editButtonProps}>
        Edit link
      </button>

      {asButton}

      <Separator orientation="vertical" />

      <LinkOpenButton />

      <Separator orientation="vertical" />

      <button
        className={buttonVariants({
          size: 'sm',
          variant: 'ghost',
        })}
        type="button"
        {...unlinkButtonProps}
      >
        <Unlink width={18} />
      </button>
    </div>
  );

  return (
    <>
      <div ref={insertRef} className={popoverVariants()} {...insertProps}>
        {input}
      </div>

      <div ref={editRef} className={popoverVariants()} {...editProps}>
        {editContent}
      </div>
    </>
  );
};

const LinkOpenButton = () => {
  const editor = useEditorRef();
  const selection = useEditorSelection();

  const attributes = useMemo(
    () => {
      const entry = editor.api.node<TLinkElement>({
        match: { type: editor.getType(KEYS.link) },
      });
      if (!entry) {
        return {};
      }
      const [element] = entry;
      return getLinkAttributes(editor, element);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, selection],
  );

  return (
    <a
      {...attributes}
      className={buttonVariants({
        size: 'sm',
        variant: 'ghost',
      })}
      onMouseOver={(e) => {
        e.stopPropagation();
      }}
      aria-label="Open link in a new tab"
      target="_blank"
    >
      <ExternalLink width={18} />
    </a>
  );
};
